<script lang="ts">
	import {
		type Achievement,
		AchievementContentValidSource,
		type AchievementHeadlineInfo
	} from '../../../domain/Achievement';
	import AchievementProgress from '$components/home/achievement/elements/AchievementProgress.svelte';
	import AchievementBody from '$components/home/achievement/elements/AchievementBody.svelte';
	import {
		calculateExpirationStatus,
		formatPreviousDayForAriaLabel,
		formatPreviousDayWithDayAndMonth
	} from '$services/DateFormatter';
	import { ExpiryStatus } from '../../../domain/ExpiryStatus';
	import { instructionSheet } from '$components/home/SheetData';
	import AchievementFooter from '$components/home/achievement/elements/AchievementFooter.svelte';
	import AchievementBaseCard from '$components/home/achievement/AchievementBaseCard.svelte';
	import { Origin } from '../../../domain/AchievementEventRequest';
	import { browser } from '$app/environment';
	import { get } from 'svelte/store';
	import { page } from '$app/stores';
	import AchievementActivationButton from '$components/home/achievement/elements/AchievementActivationButton.svelte';

	let { achievement, index, onclick, isHorizontal = false, display, onViewPortCallback }: {
		achievement: Achievement,
		index: number,
		onclick: Function,
		isHorizontal?: boolean,
		display: 'row' | 'grid',
		onViewPortCallback?: Function,
	} = $props();

	let showAppOnlyActivateButton: boolean = $state(false);

	const getHeadline = (progressEndDate: string): AchievementHeadlineInfo => {
		const expirationStatus = calculateExpirationStatus(progressEndDate);
		if (expirationStatus === ExpiryStatus.TOMORROW) {
			return {
				text: 'Nur bis morgen'
			};
		} else if (expirationStatus === ExpiryStatus.TODAY) {
			return {
				text: 'Nur noch heute'
			};
		} else {
			const visibleDate = formatPreviousDayWithDayAndMonth(progressEndDate);
			const accesibleDate = formatPreviousDayForAriaLabel(progressEndDate);

			return {
				text: `Nur bis ${visibleDate}`,
				ariaLabel: `Nur bis ${accesibleDate}`
			};
		}
	};

	const headlineInfo: AchievementHeadlineInfo = getHeadline(achievement.content.progressEndDate);

	$effect.pre(() => {
		if (browser) {
			const isAppOnlyValidSource = achievement!.content.condition.validSources.length != 0
				? !achievement!.content.condition.validSources.includes(AchievementContentValidSource.WEB)
				: false;

			const pageStore = get(page);
			showAppOnlyActivateButton = pageStore.data.toggles.appOnlyAchievementsNewDesign.enabled == true && pageStore.data.customerOrigin == Origin.WEB && isAppOnlyValidSource;
		}
	});
</script>

<AchievementBaseCard achievement={achievement} display={display} index={index+1} {onViewPortCallback}>
	<oc-card-v2
		id="achievement-card-{index+1}"
		data-testid="achievement-created-card"
		as-button=""
		class="card"
		data-oc-sheet-v1-create={JSON.stringify(instructionSheet(achievement.achievementId, "created-instructions-sheet"))}
		onclick={() => onclick()}
	>
		<div
			class="oc-p-100 card__content"
			class:card__content--horizontal={isHorizontal}
			data-testid="achievement-created-card-content">
			<div class="card__body">
				<AchievementProgress completionGoal={achievement.content.card.completionGoal}
					currentProgress={achievement.progress.current}
					progressColor={achievement.content.card.progressBarColor} />
				<AchievementBody
					description={achievement.content.card.description}
					title={achievement.content.card.title}>
					<div aria-label={headlineInfo.ariaLabel} class="oc-copy-75 text-secondary">
						{headlineInfo.text}
					</div>
				</AchievementBody>

			</div>
			<AchievementFooter
				compensationAmount={achievement.content.card.compensationAmount}
				compensationHeadline={'Verdiene'}
				isHorizontal={isHorizontal}>
				<AchievementActivationButton
					variant={showAppOnlyActivateButton ? "appOnlyCardActivation" : "cardActivation"}
					id={achievement.achievementId}
				/>
			</AchievementFooter>
		</div>
	</oc-card-v2>
</AchievementBaseCard>

<style lang="scss">
  @use '@otto-ec/otto-components-utils/scss/mixins' as mixins;
  @use "@otto-ec/design-tokens/tokens";

  .card {
    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;

      &--horizontal {
        @include mixins.breakpoint(mixins.$breakpoint-l) {
          flex-direction: row;
        }
      }
    }

    &__body {
      display: flex;
      align-items: center;
    }
  }

  .text-secondary {
    color: tokens.$oc-semantic-color-text-secondary
  }
</style>
